import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatCurrency } from '@gad/shared';
import { apiRequest } from '../lib/api';
import { fetchReferenceLibrary, type BrandReferenceLibrary } from '../lib/brandLibrary';
import { postAdCreatorGenerateFull, postAdCreatorValidate, waitForAdCreatorGenerationResult } from '../lib/adCreator';

interface Keyword {
    keyword_text: string;
    match_type: string;
    status: string;
}

interface Criminal {
    id: number;
    asset_text: string;
    field_type: 'HEADLINE' | 'DESCRIPTION';
    criminal_type: string;
    reason: string;
    campaign_name: string;
    ad_group_name: string;
    ad_group_id: string;
    total_impressions: number;
    total_clicks: number;
    total_conversions: number;
    total_cost_micros: number;
    cpl: number | null;
    ctr: number;
    cpc: number;
    ai_suggested_text?: string;
    human_decision: string;
    keywords: Keyword[];
}

type SpecializationType = 'Persona特式化' | '活動特式化' | '見證代言特式化';

interface AdGroupDetails {
    ad_group_id: string;
    ad_group_name: string;
    campaign_name: string;
    keywords: Array<{ keyword_text: string }>;
    inferred_persona: { id: string; name: string; allScenes: string[] };
}

interface OptimizationBrief {
    suggestedSpecialization: SpecializationType | null;
}

interface GeneratedHeadlineAsset {
    id: string;
    type: 'H1' | 'H2' | 'H3';
    text: string;
}

interface GeneratedDescriptionAsset {
    id: string;
    text: string;
}

interface GeneratedAssets {
    headlines: GeneratedHeadlineAsset[];
    descriptions: GeneratedDescriptionAsset[];
}

interface AssetValidationRow {
    text: string;
    isValid: boolean;
    issues: string[];
}

interface AssetValidationSummary {
    headlines: AssetValidationRow[];
    descriptions: AssetValidationRow[];
}

const SPECIALIZATION_OPTIONS: SpecializationType[] = ['Persona特式化', '活動特式化', '見證代言特式化'];
function inferPersona(campaignName: string, adGroupName: string): string {
    const text = `${campaignName} ${adGroupName}`.toLowerCase();

    if (text.includes('產後')) return '產後媽媽';
    if (text.includes('久坐') || text.includes('上班族')) return '久坐上班族';
    if (text.includes('美食')) return '美食愛好者';
    if (text.includes('年輕')) return '年輕女性';
    if (text.includes('術後')) return '術後恢復者';
    if (text.includes('莊碧玉') || text.includes('璟宣') || text.includes('廖苡喬')) return '品牌忠誠用戶';
    if (text.includes('競品') || text.includes('黛莉貝爾') || text.includes('維娜斯')) return '競品比較者';

    return '一般女性消費者';
}

function toCopyPlainText(assets: GeneratedAssets): string {
    const headlines = assets.headlines.map((x, i) => `${i + 1}. ${x.text}`).join('\n');
    const descriptions = assets.descriptions.map((x, i) => `${i + 1}. ${x.text}`).join('\n');
    return `Headlines (15)\n${headlines}\n\nDescriptions (4)\n${descriptions}`;
}

function toCopyTableText(assets: GeneratedAssets): string {
    const headlineTexts = assets.headlines.slice(0, 15).map(h => h.text);
    const descriptionTexts = assets.descriptions.slice(0, 4).map(d => d.text);

    while (headlineTexts.length < 15) headlineTexts.push('');
    while (descriptionTexts.length < 4) descriptionTexts.push('');

    const headers = [
        ...Array.from({ length: 15 }, (_, i) => `Headline ${i + 1}`),
        ...Array.from({ length: 4 }, (_, i) => `Description ${i + 1}`)
    ];

    return `${headers.join('\t')}\n${[...headlineTexts, ...descriptionTexts].join('\t')}`;
}

export function CopyEditor() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);

    const [criminal, setCriminal] = useState<Criminal | null>(null);
    const [newText, setNewText] = useState('');
    const [persona, setPersona] = useState('');
    const [keywords, setKeywords] = useState<string[]>([]);
    const [scene, setScene] = useState('');
    const [availableScenes, setAvailableScenes] = useState<string[]>([]);
    const [metaPostUrl, setMetaPostUrl] = useState('');
    const [metaPostText, setMetaPostText] = useState('');
    const [referenceLibrary, setReferenceLibrary] = useState<BrandReferenceLibrary | null>(null);
    const [selectedCelebrityNames, setSelectedCelebrityNames] = useState<string[]>([]);
    const [selectedActivityNames, setSelectedActivityNames] = useState<string[]>([]);
    const [selectedSpecialization, setSelectedSpecialization] = useState<SpecializationType>('Persona特式化');
    const [generatedAssets, setGeneratedAssets] = useState<GeneratedAssets | null>(null);
    const [generatedValidation, setGeneratedValidation] = useState<AssetValidationSummary | null>(null);
    const [currentValidation, setCurrentValidation] = useState<AssetValidationRow | null>(null);

    const [error, setError] = useState('');
    const [copyMessage, setCopyMessage] = useState('');

    useEffect(() => {
        void loadCriminal();
    }, [id]);

    useEffect(() => {
        void loadReferenceLibrary();
    }, []);

    async function loadReferenceLibrary() {
        try {
            setReferenceLibrary(await fetchReferenceLibrary());
        } catch (err) {
            setError(err instanceof Error ? err.message : '無法載入品牌資料庫');
        }
    }

    function toggleNamedValue(target: string, values: string[], setter: (values: string[]) => void) {
        if (values.includes(target)) {
            setter(values.filter((value) => value !== target));
            return;
        }
        setter([...values, target]);
    }

    async function loadCriminal() {
        try {
            const data = await apiRequest<Criminal>(`/api/diagnosis/criminals/${id}`);

            if (!data.success || !data.data) {
                setError('找不到該資產');
                return;
            }

            const c = data.data;
            setCriminal(c);
            setNewText(c.ai_suggested_text || '');
            setMetaPostUrl('');
            setMetaPostText('');

            const inferredPersona = inferPersona(c.campaign_name, c.ad_group_name);
            setPersona(inferredPersona);
            if (c.keywords && c.keywords.length > 0) {
                setKeywords(c.keywords.map((k: Keyword) => k.keyword_text));
            }

            try {
                const [detailsRes, briefRes] = await Promise.all([
                    apiRequest<AdGroupDetails>(`/api/optimizer/ad-groups/${c.ad_group_id}/details`),
                    apiRequest<OptimizationBrief>(`/api/optimizer/ad-groups/${c.ad_group_id}/optimization-brief`)
                ]);

                if (detailsRes.success && detailsRes.data) {
                    setPersona(detailsRes.data.inferred_persona.name);
                    setKeywords(detailsRes.data.keywords.map(k => k.keyword_text));
                    setAvailableScenes(detailsRes.data.inferred_persona.allScenes || []);
                }

                if (briefRes.success && briefRes.data?.suggestedSpecialization) {
                    setSelectedSpecialization(briefRes.data.suggestedSpecialization);
                }
            } catch (optimizerErr) {
                console.warn('[CopyEditor] Failed to sync optimizer context', optimizerErr);
            }
        } catch (err) {
            console.error(err);
            setError('載入失敗');
        } finally {
            setLoading(false);
        }
    }

    async function generateAssets() {
        if (!criminal) return;
        if (keywords.length === 0) {
            setError('此廣告組合沒有可用關鍵字，無法生成文案');
            return;
        }

        if (selectedSpecialization === '見證代言特式化' && (referenceLibrary?.celebrities.active.length || 0) === 0) {
            setError('品牌資料庫目前沒有可用名人素材，請先到品牌資料庫建立');
            return;
        }

        try {
            setGenerating(true);
            setError('');
            setCopyMessage('');
            setGeneratedAssets(null);
            setGeneratedValidation(null);

            const start = await postAdCreatorGenerateFull<GeneratedAssets>({
                adGroupId: criminal.ad_group_id,
                keywords,
                persona,
                specialization: selectedSpecialization,
                scene: scene || undefined,
                celebrityNames: selectedCelebrityNames,
                activityNames: selectedActivityNames,
                metaPostUrl,
                metaPostText
            });

            if (!start.success || !start.generationId) {
                setError(start.error || '生成任務建立失敗');
                return;
            }

            const result = await waitForAdCreatorGenerationResult<GeneratedAssets>(start.generationId);
            if (!result.success || result.status !== 'completed' || !result.generated) {
                setError(result.error || '生成失敗');
                return;
            }

            setGeneratedAssets(result.generated);
            await validateGeneratedAssets(result.generated);
        } catch (err) {
            console.error(err);
            setError('生成文案時發生錯誤');
        } finally {
            setGenerating(false);
        }
    }

    function applyGeneratedText(text: string) {
        setNewText(text);
        void validateSingleText(text);
    }

    async function validateGeneratedAssets(assets: GeneratedAssets) {
        const validation = await postAdCreatorValidate({
            headlines: assets.headlines.map((item) => item.text),
            descriptions: assets.descriptions.map((item) => item.text),
            headlineTypes: assets.headlines.map((item) => item.type),
            specialization: selectedSpecialization,
            celebrityNames: selectedCelebrityNames.length > 0 ? selectedCelebrityNames : undefined,
            activityNames: selectedActivityNames.length > 0 ? selectedActivityNames : undefined
        });

        if (validation.success && validation.validation) {
            setGeneratedValidation(validation.validation);
        }
    }

    async function validateSingleText(textOverride?: string) {
        if (!criminal) return;

        const candidate = (textOverride ?? newText).trim();
        if (!candidate) {
            setCurrentValidation(null);
            return;
        }

        const validation = await postAdCreatorValidate({
            headlines: criminal.field_type === 'HEADLINE' ? [candidate] : undefined,
            descriptions: criminal.field_type === 'DESCRIPTION' ? [candidate] : undefined,
            headlineTypes: criminal.field_type === 'HEADLINE' ? ['H1'] : undefined,
            specialization: selectedSpecialization,
            celebrityNames: selectedCelebrityNames.length > 0 ? selectedCelebrityNames : undefined,
            activityNames: selectedActivityNames.length > 0 ? selectedActivityNames : undefined
        });

        if (!validation.success || !validation.validation) {
            setCurrentValidation(null);
            return;
        }

        const row = criminal.field_type === 'HEADLINE'
            ? validation.validation.headlines[0]
            : validation.validation.descriptions[0];
        setCurrentValidation(row || null);
    }

    async function copyPlain() {
        if (!generatedAssets) return;
        await navigator.clipboard.writeText(toCopyPlainText(generatedAssets));
        setCopyMessage('已複製：純文字格式');
    }

    async function copyTable() {
        if (!generatedAssets) return;
        await navigator.clipboard.writeText(toCopyTableText(generatedAssets));
        setCopyMessage('已複製：表格（TSV）格式');
    }

    async function handleDecision(decision: 'APPROVED' | 'REJECTED') {
        if (!criminal) return;

        try {
            setSaving(true);
            const data = await apiRequest(`/api/diagnosis/criminals/${criminal.id}`, {
                method: 'PATCH',
                body: JSON.stringify({
                    decision,
                    modifiedText: decision === 'APPROVED' ? newText : undefined
                })
            });

            if (data.success) navigate('/');
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    }

    if (loading) return <div className="loading"><div className="spinner"></div></div>;
    if (!criminal) return <div className="card">{error || '找不到該資產'}</div>;

    return (
        <div>
            <button className="btn btn-outline" onClick={() => navigate('/')} style={{ marginBottom: '1.5rem' }}>
                ← 返回清單
            </button>

            <h2 style={{ marginBottom: '1.5rem' }}>✏️ 編輯文案</h2>

            <div className="card">
                <h3 className="card-title" style={{ marginBottom: '1rem' }}>📌 當前戰犯文案</h3>
                <div style={{ marginBottom: '0.7rem', color: 'var(--color-text-secondary)' }}>
                    {criminal.campaign_name} › {criminal.ad_group_name}
                </div>
                <div style={{ marginBottom: '0.8rem' }}>
                    <span className={`criminal-type ${criminal.criminal_type === 'IMMEDIATE_REPLACE' ? 'immediate' : 'quality'}`}>
                        {criminal.criminal_type === 'IMMEDIATE_REPLACE' ? '立即替換' : '品質警告'}
                    </span>
                    <span style={{ marginLeft: '0.65rem', color: 'var(--color-text-secondary)' }}>[{criminal.field_type}]</span>
                </div>
                <div style={{ background: 'rgba(220, 38, 38, 0.1)', padding: '0.9rem', borderRadius: '8px', marginBottom: '0.85rem' }}>
                    "{criminal.asset_text}"
                </div>
                <div style={{ display: 'flex', gap: '0.9rem', flexWrap: 'wrap', color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
                    <span>🖱️ 點擊 {criminal.total_clicks.toLocaleString()}</span>
                    <span>📈 CTR {criminal.ctr.toFixed(2)}%</span>
                </div>
            </div>

            <div className="card">
                <h3 className="card-title" style={{ marginBottom: '1rem' }}>⚙️ 生成設定（對齊 Optimizer）</h3>

                <div style={{ marginBottom: '0.8rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.45rem', color: 'var(--color-text-secondary)' }}>Persona</label>
                    <input
                        type="text"
                        value={persona}
                        onChange={(e) => setPersona(e.target.value)}
                        style={{ width: '100%', padding: '0.7rem', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-text)' }}
                    />
                </div>

                <div style={{ marginBottom: '0.8rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.45rem', color: 'var(--color-text-secondary)' }}>Keywords</label>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {keywords.length > 0 ? keywords.map((kw, i) => (
                            <span key={i} style={{ background: 'var(--color-primary)', color: '#fff', borderRadius: '999px', padding: '0.2rem 0.65rem', fontSize: '0.82rem' }}>
                                {kw}
                            </span>
                        )) : <span style={{ color: 'var(--color-text-secondary)' }}>無關鍵字資料</span>}
                    </div>
                </div>

                <div style={{ marginBottom: '0.8rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.45rem', color: 'var(--color-text-secondary)' }}>使用場景（選填，可手動輸入）</label>
                    <input
                        type="text"
                        list="scene-list-editor"
                        value={scene}
                        onChange={(e) => setScene(e.target.value)}
                        placeholder="例：婚禮/婚宴、婚紗試穿、外出活動、居家休閒..."
                        style={{ width: '100%', padding: '0.7rem', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-text)' }}
                    />
                    <datalist id="scene-list-editor">
                        {availableScenes.map(s => <option key={s} value={s} />)}
                    </datalist>
                    <div style={{ marginTop: '0.4rem', color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                        留空則由 AI 自動判斷。
                    </div>
                </div>

                <div style={{ marginBottom: '0.8rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.45rem', color: 'var(--color-text-secondary)' }}>Meta 貼文（選填）</label>
                    <input
                        type="url"
                        value={metaPostUrl}
                        onChange={(e) => setMetaPostUrl(e.target.value)}
                        placeholder="貼文連結（FB/IG）"
                        style={{ width: '100%', padding: '0.7rem', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-text)', marginBottom: '0.5rem' }}
                    />
                    <textarea
                        value={metaPostText}
                        onChange={(e) => setMetaPostText(e.target.value)}
                        placeholder="貼文內容（建議貼上，作為文案基底）"
                        rows={3}
                        style={{ width: '100%', padding: '0.7rem', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-text)', resize: 'vertical' }}
                    />
                </div>

                <div>
                    <label style={{ display: 'block', marginBottom: '0.45rem', color: 'var(--color-text-secondary)' }}>特式化</label>
                    <select
                        value={selectedSpecialization}
                        onChange={(e) => setSelectedSpecialization(e.target.value as SpecializationType)}
                        style={{ width: '100%', padding: '0.7rem', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: '8px', color: 'var(--color-text)' }}
                    >
                        {SPECIALIZATION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                </div>

                <div style={{ marginTop: '0.8rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.45rem', color: 'var(--color-text-secondary)' }}>
                        品牌資料庫名人素材（未選時會使用全部可用名人）
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {(referenceLibrary?.celebrities.active || []).map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                onClick={() => toggleNamedValue(item.name, selectedCelebrityNames, setSelectedCelebrityNames)}
                                className="btn"
                                style={{
                                    background: selectedCelebrityNames.includes(item.name) ? 'var(--color-primary)' : 'var(--color-bg-secondary)',
                                    color: selectedCelebrityNames.includes(item.name) ? 'white' : 'var(--color-text)',
                                    border: '1px solid var(--color-border)'
                                }}
                            >
                                {item.name}
                            </button>
                        ))}
                    </div>
                </div>

                <div style={{ marginTop: '0.8rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.45rem', color: 'var(--color-text-secondary)' }}>
                        品牌資料庫活動素材（未選時會使用全部可用活動）
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {(referenceLibrary?.activities.active || []).map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                onClick={() => toggleNamedValue(item.name, selectedActivityNames, setSelectedActivityNames)}
                                className="btn"
                                style={{
                                    background: selectedActivityNames.includes(item.name) ? 'var(--color-primary)' : 'var(--color-bg-secondary)',
                                    color: selectedActivityNames.includes(item.name) ? 'white' : 'var(--color-text)',
                                    border: '1px solid var(--color-border)'
                                }}
                            >
                                {item.name}
                            </button>
                        ))}
                    </div>
                    {referenceLibrary && (referenceLibrary.summary.expiredCelebrities > 0 || referenceLibrary.summary.expiredActivities > 0) && (
                        <div style={{ marginTop: '0.5rem', color: 'var(--color-warning)', fontSize: '0.85rem' }}>
                            品牌資料庫有過期素材，系統不會使用，請至品牌資料庫清理。
                        </div>
                    )}
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">🤖 生成完整 RSA（15 + 4）</h3>
                    <button className="btn btn-primary" onClick={generateAssets} disabled={generating}>
                        {generating ? '生成中...' : '生成'}
                    </button>
                </div>

                {error && <div style={{ color: 'var(--color-error)', marginBottom: '0.8rem' }}>⚠️ {error}</div>}

                {generatedAssets && (
                    <div>
                        <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.8rem' }}>
                            <button className="btn btn-outline" onClick={copyPlain}>複製純文字</button>
                            <button className="btn btn-outline" onClick={copyTable}>複製表格</button>
                            {copyMessage && <span style={{ color: 'var(--color-success)', alignSelf: 'center', fontSize: '0.88rem' }}>{copyMessage}</span>}
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <h4 style={{ marginBottom: '0.45rem' }}>Headlines（{generatedAssets.headlines.length}/15）</h4>
                            <div style={{ display: 'grid', gap: '0.45rem' }}>
                                {generatedAssets.headlines.map((headline, i) => (
                                    <div
                                        key={headline.id}
                                        style={{
                                            display: 'flex',
                                            gap: '0.5rem',
                                            alignItems: 'center',
                                            background: 'var(--color-bg-secondary)',
                                            border: '1px solid var(--color-border)',
                                            borderRadius: '6px',
                                            padding: '0.45rem 0.55rem'
                                        }}
                                    >
                                        <div style={{ minWidth: '4.5rem', fontSize: '0.82rem', color: 'var(--color-text-secondary)' }}>
                                            {i + 1}. [{headline.type}]
                                        </div>
                                        <div style={{ flex: 1, fontSize: '0.9rem' }}>{headline.text}</div>
                                        <button className="btn btn-outline" type="button" onClick={() => applyGeneratedText(headline.text)}>
                                            套用
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <h4 style={{ marginBottom: '0.45rem' }}>Descriptions（{generatedAssets.descriptions.length}/4）</h4>
                            <div style={{ display: 'grid', gap: '0.45rem' }}>
                                {generatedAssets.descriptions.map((description, i) => (
                                    <div
                                        key={description.id}
                                        style={{
                                            display: 'flex',
                                            gap: '0.5rem',
                                            alignItems: 'center',
                                            background: 'var(--color-bg-secondary)',
                                            border: '1px solid var(--color-border)',
                                            borderRadius: '6px',
                                            padding: '0.45rem 0.55rem'
                                        }}
                                    >
                                        <div style={{ minWidth: '2rem', fontSize: '0.82rem', color: 'var(--color-text-secondary)' }}>
                                            {i + 1}.
                                        </div>
                                        <div style={{ flex: 1, fontSize: '0.9rem' }}>{description.text}</div>
                                        <button className="btn btn-outline" type="button" onClick={() => applyGeneratedText(description.text)}>
                                            套用
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {generatedValidation && (
                            <div style={{ marginBottom: '1rem' }}>
                                <h4 style={{ marginBottom: '0.45rem' }}>共用規則驗證</h4>
                                <div style={{ display: 'grid', gap: '0.3rem' }}>
                                    {generatedValidation.headlines.map((row, i) => (
                                        <div key={`gh-${i}`} style={{ fontSize: '0.86rem', color: row.isValid ? 'var(--color-success)' : 'var(--color-warning)' }}>
                                            H{i + 1}: {row.isValid ? '通過' : row.issues.join('、')}
                                        </div>
                                    ))}
                                    {generatedValidation.descriptions.map((row, i) => (
                                        <div key={`gd-${i}`} style={{ fontSize: '0.86rem', color: row.isValid ? 'var(--color-success)' : 'var(--color-warning)' }}>
                                            D{i + 1}: {row.isValid ? '通過' : row.issues.join('、')}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="card">
                <h3 className="card-title" style={{ marginBottom: '0.8rem' }}>📝 新文案</h3>
                <textarea
                    value={newText}
                    onChange={(e) => setNewText(e.target.value)}
                    placeholder="輸入或從上方生成結果套用"
                    style={{
                        width: '100%',
                        minHeight: '96px',
                        padding: '0.9rem',
                        background: 'rgba(16, 185, 129, 0.1)',
                        border: '1px solid var(--color-success)',
                        borderRadius: '8px',
                        color: 'var(--color-text)',
                        fontSize: '1rem',
                        resize: 'vertical'
                    }}
                />
                <div style={{ marginTop: '0.55rem', color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                    字數 {newText.length}
                </div>
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                    <button className="btn btn-outline" type="button" onClick={() => void validateSingleText()}>
                        驗證目前文案
                    </button>
                    {currentValidation && (
                        <span style={{ color: currentValidation.isValid ? 'var(--color-success)' : 'var(--color-warning)', fontSize: '0.88rem' }}>
                            {currentValidation.isValid ? '符合共用規則' : currentValidation.issues.join('、')}
                        </span>
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button className="btn btn-outline" onClick={() => handleDecision('REJECTED')} disabled={saving}>略過</button>
                <button className="btn btn-primary" onClick={() => handleDecision('APPROVED')} disabled={saving || !newText.trim()}>
                    {saving ? '儲存中...' : '採用並儲存'}
                </button>
            </div>
        </div>
    );
}
